import { useCallback, useEffect, useRef, useState } from "react"
import type { SearchMessage, SearchResult } from "@/schema/search"
import type { Subscription } from "@rails/actioncable"

import { getConsumer } from "@/lib/cable"
import { useBookId } from "@/hooks/use-book-id"
import { useDebounce } from "@/hooks/use-debounce"
import { useDevAuth as useAuth } from "@/hooks/use-dev-auth"

interface UseGlobalSearchOptions {
  allowedTypes?: readonly SearchResult["type"][]
  bookIdOverride?: string
  bookScopedUsers?: boolean
}

export function useGlobalSearch(options?: UseGlobalSearchOptions) {
  const { getToken } = useAuth()
  const urlBookId = useBookId()
  const bookId = options?.bookIdOverride ?? urlBookId
  const [searchQuery, setSearchQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const subscriptionRef = useRef<Subscription | null>(null)
  const accumulatedResultsRef = useRef<Map<string, SearchResult>>(new Map())
  const currentQueryRef = useRef<string>("")
  const allowedTypes = options?.allowedTypes
  const allowedTypesRef = useRef(allowedTypes)
  const bookScopedUsers = options?.bookScopedUsers ?? false
  const bookScopedUsersRef = useRef(bookScopedUsers)

  useEffect(() => {
    allowedTypesRef.current = allowedTypes
  }, [allowedTypes])

  useEffect(() => {
    bookScopedUsersRef.current = bookScopedUsers
  }, [bookScopedUsers])

  const performSearch = useCallback((query: string) => {
    if (subscriptionRef.current && query.trim()) {
      setIsSearching(true)
      accumulatedResultsRef.current.clear()
      setResults([])
      currentQueryRef.current = query
      subscriptionRef.current.perform("search", {
        query,
        allowed_types: allowedTypesRef.current,
        book_scoped_users: bookScopedUsersRef.current,
      })
    } else if (!query.trim()) {
      setIsSearching(false)
      accumulatedResultsRef.current.clear()
      setResults([])
      currentQueryRef.current = ""
    }
  }, [])

  const debouncedSearch = useDebounce(performSearch, 300)

  useEffect(() => {
    if (!bookId) return

    const setupSubscription = async () => {
      const token = await getToken()
      if (!token) return

      const consumer = getConsumer(token)

      // Clean up any existing subscription first
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }

      subscriptionRef.current = consumer.subscriptions.create(
        {
          channel: "SearchChannel",
          book_id: bookId,
        },
        {
          received: (message: SearchMessage) => {
            if (message.query === currentQueryRef.current) {
              message.results.forEach((result) => {
                // Filter by allowed types if specified
                const currentAllowedTypes = allowedTypesRef.current
                if (!currentAllowedTypes || currentAllowedTypes.includes(result.type)) {
                  const key = `${result.type}-${result.id}`
                  accumulatedResultsRef.current.set(key, result)
                }
              })

              const sortedResults = Array.from(accumulatedResultsRef.current.values()).sort((a, b) => b.score - a.score)

              setResults(sortedResults)
              setIsSearching(false)
            }
          },
        }
      )
    }

    setupSubscription()

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
    }
  }, [bookId, getToken])

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query)
      if (!query.trim()) {
        setIsSearching(false)
        accumulatedResultsRef.current.clear()
        setResults([])
        currentQueryRef.current = ""
      } else {
        setIsSearching(true)
        debouncedSearch(query)
      }
    },
    [debouncedSearch]
  )

  const clearSearch = useCallback(() => {
    setSearchQuery("")
    setResults([])
    setIsSearching(false)
    accumulatedResultsRef.current.clear()
    currentQueryRef.current = ""
  }, [])

  return {
    searchQuery,
    results,
    isSearching,
    handleSearchChange,
    clearSearch,
    isConnected: !!subscriptionRef.current,
  }
}
